import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkActionModal = ({ isOpen, onClose, onConfirm, action, selectedCount }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  if (!isOpen) return null;

  const actionConfig = {
    activate: {
      title: 'Activate Users',
      description: `Are you sure you want to activate ${selectedCount} user(s)? This will enable their access to the system.`,
      confirmLabel: 'Activate Users',
      icon: 'CheckCircle',
      iconColor: 'text-success',
      variant: 'default',
      requireConfirmation: false
    },
    deactivate: {
      title: 'Deactivate Users',
      description: `Are you sure you want to deactivate ${selectedCount} user(s)? This will disable their access to the system but preserve their data.`,
      confirmLabel: 'Deactivate Users',
      icon: 'XCircle',
      iconColor: 'text-warning',
      variant: 'warning',
      requireConfirmation: false
    },
    delete: {
      title: 'Delete Users',
      description: `This action will permanently delete ${selectedCount} user(s) and all associated data. This cannot be undone.`,
      confirmLabel: 'Delete Users',
      icon: 'Trash2',
      iconColor: 'text-error',
      variant: 'destructive',
      requireConfirmation: true,
      confirmationText: 'DELETE'
    }
  };

  const config = actionConfig?.[action] || actionConfig?.activate;

  const handleConfirm = async () => {
    if (config?.requireConfirmation && confirmText !== config?.confirmationText) {
      return;
    }

    setIsProcessing(true);
    try {
      await onConfirm(action);
      onClose();
    } catch (error) {
      console.error('Bulk action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  const isConfirmDisabled = config?.requireConfirmation && confirmText !== config?.confirmationText;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-300 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md">
        <div className="flex items-center gap-4 p-6 border-b border-border">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            action === 'delete' ? 'bg-error/10' : 
            action === 'deactivate' ? 'bg-warning/10' : 'bg-success/10'
          }`}>
            <Icon name={config?.icon} size={24} className={config?.iconColor} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-card-foreground">{config?.title}</h2>
            <p className="text-sm text-muted-foreground">{selectedCount} user(s) selected</p>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-foreground">{config?.description}</p>

          {config?.requireConfirmation && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Type <span className="font-mono bg-muted px-1 rounded">{config?.confirmationText}</span> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                placeholder={config?.confirmationText}
              />
            </div>
          )}

          {action === 'delete' && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-error mb-1">Warning: Permanent Action</h4>
                  <ul className="text-sm text-error/80 space-y-1">
                    <li>• User accounts will be permanently deleted</li>
                    <li>• All user data and permissions will be lost</li>
                    <li>• Document ownership will be transferred to system</li>
                    <li>• This action cannot be reversed</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {action === 'deactivate' && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-warning mb-1">Deactivation Effects</h4>
                  <ul className="text-sm text-warning/80 space-y-1">
                    <li>• Users will be unable to log in</li>
                    <li>• Active sessions will be terminated</li>
                    <li>• Data and permissions will be preserved</li>
                    <li>• Users can be reactivated later</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant={config?.variant}
            onClick={handleConfirm}
            disabled={isConfirmDisabled || isProcessing}
            loading={isProcessing}
            iconName={config?.icon}
            iconPosition="left"
          >
            {config?.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkActionModal;